import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import {
  Puzzle,
  Download,
  ExternalLink,
  Chrome,
  Flame,
  Compass,
  MousePointerClick,
  PanelRight,
  Command,
  Keyboard,
  Globe,
  Wand2,
  Search,
  Layers,
  type LucideIcon,
} from 'lucide-react';
import { PublicShell } from '../components/PublicShell';
import { Section } from '../components/public/Section';
import { FeatureCard } from '../components/public/FeatureCard';
import { usePageMeta } from '../hooks/usePageMeta';
import { publicUrl, hreflangAlternates, activeLang, ogLocaleOf } from '../lib/publicSeo';

const ROUTE = '/extension';
const OG_IMAGE = 'https://markflow.fyi/og.png';

// Mozilla AMO is the only published store listing today (Chrome Web Store and
// Opera are still in review — see DownloadPage). Locale-aware so UA visitors
// land on the localized listing; Mozilla resolves both slugs to the same add-on.
function amoUrlForLocale(lang: string): string {
  const locale = lang.toLowerCase().startsWith('uk') ? 'uk' : 'en-US';
  return `https://addons.mozilla.org/${locale}/firefox/addon/markflow-fyi/`;
}

// The extension's UI surfaces, ordered as showcased. Copy lives in i18n under
// `extension.surfaces.items.<key>`; icons stay in code.
const SURFACES: { key: string; icon: LucideIcon }[] = [
  { key: 'popup', icon: MousePointerClick },
  { key: 'sidePanel', icon: PanelRight },
  { key: 'palette', icon: Command },
  { key: 'hotkey', icon: Keyboard },
  { key: 'crossBrowser', icon: Globe },
];

// Positioning highlights (scope guardrail: AI organize, semantic search,
// per-profile Spaces — no team/publish/RSS).
const HIGHLIGHTS: { key: string; icon: LucideIcon }[] = [
  { key: 'aiOrganize', icon: Wand2 },
  { key: 'semanticSearch', icon: Search },
  { key: 'spaces', icon: Layers },
];

// Primary install CTA pair, reused by the hero and the closing band. Primary
// points at /download (the universal install hub — unpacked builds for every
// Chromium browser plus the live AMO link); secondary is the one published
// store, Firefox AMO.
function InstallCtas() {
  const { t, i18n } = useTranslation();
  return (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
      <Link
        href="/download"
        className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-fuchsia-500 hover:from-violet-700 hover:to-fuchsia-600 transition-colors shadow-sm w-full sm:w-auto outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-50 dark:focus-visible:ring-offset-zinc-950"
      >
        <Download className="w-4 h-4" aria-hidden />
        {t('extension.hero.ctaPrimary')}
      </Link>
      <a
        href={amoUrlForLocale(i18n.language)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-zinc-700 dark:text-zinc-200 border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors w-full sm:w-auto outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-50 dark:focus-visible:ring-offset-zinc-950"
      >
        <ExternalLink className="w-4 h-4" aria-hidden />
        {t('extension.hero.ctaFirefox')}
      </a>
    </div>
  );
}

// Per-store availability row. Firefox is published (live AMO button); Chrome Web
// Store and Opera are still in review, shown with a "coming soon" pill so the
// copy stays honest (mirrors DownloadPage's state).
function StoreRow() {
  const { t, i18n } = useTranslation();
  const stores: { key: 'chrome' | 'firefox' | 'opera'; icon: LucideIcon }[] = [
    { key: 'chrome', icon: Chrome },
    { key: 'firefox', icon: Flame },
    { key: 'opera', icon: Compass },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {stores.map(({ key, icon: Icon }) => (
        <div
          key={key}
          className="flex flex-col items-center text-center gap-3 rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6"
        >
          <Icon className="w-7 h-7 text-zinc-700 dark:text-zinc-300" aria-hidden />
          <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">
            {t(`extension.stores.${key}.name`)}
          </span>
          {key === 'firefox' ? (
            <a
              href={amoUrlForLocale(i18n.language)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-violet-600 to-fuchsia-500 hover:from-violet-700 hover:to-fuchsia-600 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-50 dark:focus-visible:ring-offset-zinc-950"
            >
              <ExternalLink className="w-3.5 h-3.5" aria-hidden />
              {t('extension.stores.firefox.cta')}
            </a>
          ) : (
            <span className="inline-flex items-center rounded-full bg-zinc-100 dark:bg-zinc-800 px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
              {t('extension.stores.soon')}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// Public marketing page at `/extension` — the acquisition surface for the
// MarkFlow browser extension and the target for store-listing "website" links.
// Renders in PublicShell; owns its own <main> so each section sets its rhythm.
export function ExtensionLandingPage() {
  const { t, i18n } = useTranslation();
  const lang = activeLang(i18n.resolvedLanguage);
  const pageUrl = publicUrl(ROUTE, lang);

  usePageMeta({
    title: t('extension.meta.title'),
    description: t('extension.meta.description'),
    canonical: pageUrl,
    ogTitle: t('extension.meta.ogTitle'),
    ogDescription: t('extension.meta.ogDescription'),
    ogImage: OG_IMAGE,
    ogUrl: pageUrl,
    htmlLang: lang,
    ogLocale: ogLocaleOf(lang),
    ogLocaleAlternate: ogLocaleOf(lang === 'uk' ? 'en' : 'uk'),
    alternates: hreflangAlternates(ROUTE),
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'MarkFlow — Browser Extension',
      description: t('extension.meta.ogDescription'),
      applicationCategory: 'BrowserApplication',
      operatingSystem: 'Chrome, Firefox, Opera, Brave, Vivaldi',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
      },
      url: pageUrl,
      image: OG_IMAGE,
    },
  });

  return (
    <PublicShell>
      <main className="flex-1">
        {/* Hero */}
        <section className="mx-auto max-w-3xl px-4 sm:px-6 py-20 sm:py-28 text-center space-y-6">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 dark:bg-violet-950/40 px-3 py-1 text-xs font-medium text-violet-700 dark:text-violet-300">
            <Puzzle className="w-3.5 h-3.5" aria-hidden />
            {t('extension.hero.eyebrow')}
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50 leading-tight">
            {t('extension.hero.title')}
          </h1>
          <p className="text-lg sm:text-xl text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            {t('extension.hero.subtitle')}
          </p>
          <div className="pt-2">
            <InstallCtas />
          </div>
          <p className="text-xs text-zinc-500">{t('extension.hero.reassurance')}</p>
        </section>

        {/* Per-store availability */}
        <Section className="pt-0" width="narrow">
          <StoreRow />
          <p className="mt-4 text-center text-xs text-zinc-500">
            {t('extension.stores.devHint')}{' '}
            <Link
              href="/download"
              className="font-medium text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300 transition-colors"
            >
              {t('extension.stores.devHintLink')}
            </Link>
          </p>
        </Section>

        {/* Surfaces — the extension's UI built into the browser */}
        <Section
          eyebrow={t('extension.surfaces.eyebrow')}
          title={t('extension.surfaces.title')}
          subtitle={t('extension.surfaces.subtitle')}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SURFACES.map(({ key, icon }) => (
              <FeatureCard key={key} icon={icon} title={t(`extension.surfaces.items.${key}.title`)}>
                {t(`extension.surfaces.items.${key}.copy`)}
              </FeatureCard>
            ))}
          </div>
        </Section>

        {/* Highlights — why this extension, not just another save button */}
        <Section
          className="pt-0"
          eyebrow={t('extension.highlights.eyebrow')}
          title={t('extension.highlights.title')}
          subtitle={t('extension.highlights.subtitle')}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {HIGHLIGHTS.map(({ key, icon }) => (
              <FeatureCard key={key} icon={icon} title={t(`extension.highlights.items.${key}.title`)}>
                {t(`extension.highlights.items.${key}.copy`)}
              </FeatureCard>
            ))}
          </div>
        </Section>

        {/* Closing CTA band */}
        <Section className="pb-24" width="narrow">
          <div className="rounded-3xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-6 py-12 sm:px-12 text-center space-y-5">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
              {t('extension.closingCta.title')}
            </h2>
            <p className="text-base text-zinc-600 dark:text-zinc-400 max-w-xl mx-auto leading-relaxed">
              {t('extension.closingCta.subtitle')}
            </p>
            <InstallCtas />
          </div>
        </Section>
      </main>
    </PublicShell>
  );
}
